<template>
  <div class="crew-cell">
    <div class="crew-row" v-for="(employee, index) in list" :key="index">
      <div class="crew-name">
        {{ employee.name || "--" }}
        <template v-if="employee.mobile"> - {{ employee.mobile }}</template>
      </div>

      <div class="crew-id" v-if="employee.crewId">
        Crew ID: {{ employee.crewId }}<template v-if="employee.crewCategory"> ( {{ employee.crewCategory }} )</template>
      </div>

      <div class="crew-flight" v-if="employee.flightInfo">{{ flightSummary(employee) }}</div>
      <div class="crew-flight" v-if="flightDateTime(employee)">{{ flightDateTime(employee) }}</div>
    </div>

    <div class="crew-empty" v-if="list.length === 0">--</div>
  </div>
</template>

<script>
/**
 * Crew details cell, shared by every Uber tab so the four lines and the flight
 * parsing live in one place.
 *
 * The two helpers are lifted from ToBeAssigned.vue (`checkFlightInfo` /
 * `checkFlightDateAndTime`) so the Uber tabs read identically to the old flow.
 */
export default {
  name: "CrewDetails",
  props: {
    employees: {
      type: Array,
      default: () => [],
    },
    // Used when a trip carries no employees array - a non-crew trip still needs
    // a name and number in the cell.
    fallbackName: {
      type: String,
      default: "",
    },
    fallbackMobile: {
      type: [String, Number],
      default: "",
    },
  },
  computed: {
    list() {
      if (Array.isArray(this.employees) && this.employees.length > 0) return this.employees;
      if (this.fallbackName || this.fallbackMobile) {
        return [{ name: this.fallbackName, mobile: this.fallbackMobile }];
      }
      return [];
    },
  },
  methods: {
    /**
     * "Flight No:6E1071 ... from BOM to HKT" -> "6E1071 BOM - HKT".
     * Anything that does not match is returned unchanged, which is how the
     * non-flight entries (ASBI, reschedule notices) still read sensibly.
     */
    flightSummary(employee) {
      const flightInfo = employee && employee.flightInfo;
      if (!flightInfo || typeof flightInfo !== "string") return "";

      return flightInfo.replace(
        /.*Flight No:(\S+)(?:.*?at (\S+) from (\S+)|.*?from (\S+) to (\S+))/,
        (_, flightNo, at1, from1, from2, to2) => {
          if (at1 && from1) return `${flightNo} ${at1} - ${from1}`;
          if (from2 && to2) return `${flightNo} ${from2} - ${to2}`;
          return flightNo;
        }
      );
    },

    /** Pulls the "YYYY-MM-DD HH:mm" out of the flight string, if present. */
    flightDateTime(employee) {
      const flightInfo = employee && employee.flightInfo;
      if (!flightInfo || typeof flightInfo !== "string") return null;
      const match = flightInfo.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/);
      return match ? match[0] : null;
    },
  },
};
</script>

<style lang="scss" scoped>
.crew-cell {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 260px;
  white-space: normal;
}

.crew-row {
  display: flex;
  flex-direction: column;
  gap: 1px;

  // A clubbed trip carries several crew members; keep them visually separate.
  &:not(:last-child) {
    padding-bottom: 6px;
    border-bottom: 1px dashed #ececec;
  }
}

.crew-name {
  font-size: 13px;
  font-weight: 600;
  color: #333333;
}

.crew-id {
  font-size: 11px;
  font-weight: 700;
  color: #6a6a6a;
}

.crew-flight {
  font-size: 11px;
  font-weight: 700;
  color: #000099;
}

.crew-empty {
  font-size: 13px;
  color: #8a8a8a;
}
</style>
